import { isCMPAdmin, allowedServices, isDiscontinuedService } from 'utils';

// Module-level state for announcement restrictions
let allCatalogsRestrictedFlag = false;
let restrictedCatalogsList = [];
let allCatalogsRestrictedMessage = '';
let restrictedCatalogMessages = {};

/**
 * Extracts and deduplicates restricted catalog codes from active announcements.
 * If any announcement has null/empty applicableCatalogs, all catalogs are considered restricted.
 * Also captures the announcement message associated with each restricted catalog so the
 * relevant reason can be displayed when access is blocked.
 * @param {Array} announcements - List of active announcements
 * @returns {{ allCatalogsRestricted: boolean, restrictedCatalogs: string[] }}
 */
export const setAnnouncementRestrictions = (announcements = []) => {
    const catalogSet = new Set();
    const messages = {};
    let allRestricted = false;
    let allRestrictedMessage = '';

    announcements.forEach((ann) => {
        if (!ann.applicableCatalogs || ann.applicableCatalogs.length === 0) {
            // null or empty means ALL catalogs are applicable (restricted)
            allRestricted = true;
            if (!allRestrictedMessage && ann.message) {
                allRestrictedMessage = ann.message;
            }
        } else {
            ann.applicableCatalogs.forEach((cat) => {
                catalogSet.add(cat);
                if (!messages[cat] && ann.message) {
                    messages[cat] = ann.message;
                }
            });
        }
    });

    allCatalogsRestrictedFlag = allRestricted;
    restrictedCatalogsList = [...catalogSet];
    allCatalogsRestrictedMessage = allRestrictedMessage;
    restrictedCatalogMessages = messages;

    return { allCatalogsRestricted: allCatalogsRestrictedFlag, restrictedCatalogs: restrictedCatalogsList };
};

/**
 * Returns the announcement message that restricts the given catalog, if any.
 * Catalog-specific announcement messages take precedence over a global (all catalogs) message.
 * @param {string} catalogCode
 * @returns {string} The relevant announcement message, or an empty string if none
 */
export const getAnnouncementRestrictionMessage = (catalogCode) => {
    if (restrictedCatalogMessages[catalogCode]) return restrictedCatalogMessages[catalogCode];
    if (allCatalogsRestrictedFlag) return allCatalogsRestrictedMessage;
    return '';
};

/**
 * Checks if a specific catalog is restricted by any active announcement.
 * @param {string} catalogCode
 * @returns {boolean} true if the catalog is restricted
 */
export const isAnnouncementRestricted = (catalogCode) => {
    if (allCatalogsRestrictedFlag) return true;
    return restrictedCatalogsList.includes(catalogCode);
};

/**
 * Returns the current restriction state.
 * @returns {{ allCatalogsRestricted: boolean, restrictedCatalogs: string[] }}
 */
export const getAnnouncementRestrictions = () => ({
    allCatalogsRestricted: allCatalogsRestrictedFlag,
    restrictedCatalogs: restrictedCatalogsList,
});

/**
 * Determines if a user can access (provision) a catalog.
 * Combines allowedServices, isDiscontinuedService, and announcement restriction checks.
 * CMP Admins bypass announcement restrictions.
 * @param {string} catalogCode
 * @returns {boolean} true if the catalog can be accessed
 */
export const canAccessCatalog = (catalogCode) => {
    // Check if catalog is allowed (scoped catalogs require admin/beta access)
    if (!allowedServices(catalogCode)) return false;

    // Check if catalog is discontinued (admins bypass this internally)
    if (isDiscontinuedService(catalogCode)) return false;

    // CMP Admin bypasses announcement restrictions
    if (isCMPAdmin()) return true;

    // Check announcement restrictions for non-admin users
    return !isAnnouncementRestricted(catalogCode);
};
